import React, { useMemo, useCallback, useState } from 'react';
import { View, StyleSheet, Switch, ScrollView, TouchableOpacity, Linking, Platform } from 'react-native';
import { Text } from '@/components/common/text';
import { useNotificationPreferences } from '@/hooks/residents/useNotificationPreferences';
import { useThemedStyles, type ThemeContextValue } from '@/hooks/shared/useThemeColors';
import { strings } from '@/constants/residents/strings';
import { useAppDispatch, useAppSelector } from '@/store';
import { setUser } from '@/store/slices/authSlice';
import { AuthAPI } from '@/services/residents/api/AuthAPI';
import { useFocusEffect } from '@react-navigation/native';
import { logger } from '@/utils/common/logger';

export const SettingsModalScreen: React.FC = () => {
  const { getValue, togglePreference, isFetching, isUpdating } = useNotificationPreferences();
  const { user } = useAppSelector((s) => s.auth);
  const dispatch = useAppDispatch();
  const styles = useThemedStyles(makeStyles);

  const loading = isFetching || isUpdating;

  // Roommate sharing toggles (iOS parity)
  const roommateSharingSupported = useMemo(() => !!user, [user]);
  const roommateEmailKey = 'enableRoommateEmailSharing';
  const roommatePhoneKey = 'enableRoommatePhoneSharing';
  const roommateEmailValue = !!user?.roommatesShareEmail;
  const roommatePhoneValue = !!user?.roommatesSharePhone;
  const [stagedRoommatePhone, setStagedRoommatePhone] = useState<boolean>(roommatePhoneValue);
  const [stagedRoommateEmail, setStagedRoommateEmail] = useState<boolean>(roommateEmailValue);

  // Keep staged values in sync if user changes while screen is focused
  useFocusEffect(
    useCallback(() => {
      setStagedRoommatePhone(roommatePhoneValue);
      setStagedRoommateEmail(roommateEmailValue);
    }, [roommatePhoneValue, roommateEmailValue])
  );

  // Batch roommate updates when leaving the screen (on blur)
  useFocusEffect(
    useCallback(() => {
      return () => {
        if (!user) return;
        const phoneChanged = stagedRoommatePhone !== (!!user.roommatesSharePhone);
        const emailChanged = stagedRoommateEmail !== (!!user.roommatesShareEmail);
        if (!phoneChanged && !emailChanged) return;
        const payload: any = {};
        if (phoneChanged) payload.roommatesSharePhone = stagedRoommatePhone;
        if (emailChanged) payload.roommatesShareEmail = stagedRoommateEmail;
        (async () => {
          try {
            const updated = await AuthAPI.updateUser(payload);
            dispatch(setUser(updated));
          } catch (e) {
            logger.warn('roommateSharingSaveFailed', e);
          }
        })();
      };
    }, [stagedRoommatePhone, stagedRoommateEmail, user, dispatch])
  );

  // Permission request handler for "Allow push notifications"
  const onPressAllowNotifications = useCallback(async () => {
    if (Platform.OS === 'android') {
      try {
        // Android 13+ requires POST_NOTIFICATIONS runtime permission
        const needsPermission = typeof Platform.Version === 'number' ? Platform.Version >= 33 : false;
        if (needsPermission) {
          // Avoid importing PermissionsAndroid on web by requiring at runtime inside Android branch
          const rn: any = require('react-native');
          const { PermissionsAndroid } = rn;
          if (PermissionsAndroid?.request && PermissionsAndroid?.PERMISSIONS?.POST_NOTIFICATIONS) {
            await PermissionsAndroid.request(PermissionsAndroid.PERMISSIONS.POST_NOTIFICATIONS);
          }
        }
      } catch (e) {
        logger.warn('settingsPermissionCheckFailed', e);
      }
    } else {
      try {
        const canOpen = await Linking.canOpenURL('app-settings:');
        if (canOpen) {
          await Linking.openURL('app-settings:');
        } else {
          await Linking.openSettings();
        }
      } catch {
        try { await Linking.openSettings(); } catch (e) { logger.warn('openSettingsFailed', e); }
      }
    }
  }, []);

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      {/* General Settings section */}
      <Text style={styles.sectionTitle}>{strings.notifications.section.set.toUpperCase()}</Text>
      <TouchableOpacity
        style={styles.linkRow}
        onPress={onPressAllowNotifications}
        accessibilityRole="button"
        accessibilityLabel={'SettingsCell_notifications.op.allow'}
      >
        <Text style={[styles.label, styles.linkLabel]}>{strings.notifications.options.allow}</Text>
      </TouchableOpacity>

      {/* Notifications section */}
      <Text style={[styles.sectionTitle, styles.notificationsHeader]}>{strings.notifications.section.not.toUpperCase()}</Text>
      <View style={styles.row}>
        <Text style={styles.label}>{strings.notifications.options.community}</Text>
        <Switch
          value={!!user?.enablePushNotificationMessages}
          onValueChange={async (val) => {
            try {
              const updated = await AuthAPI.updateUser({ enablePushNotificationMessages: val });
              dispatch(setUser(updated));
            } catch (e) {
              logger.warn('settingsToggleFailed', e);
            }
          }}
          disabled={loading}
        />
      </View>

      {/* Roommate sharing section (matches iOS Settings) */}
      {roommateSharingSupported && (
        <>
          <Text style={[styles.sectionTitle, styles.roommateHeader]}>{strings.notifications.roommates.toUpperCase()}</Text>
          <View style={styles.row}>
            <Text style={styles.label}>{strings.notifications.roommatesPhone}</Text>
            <Switch
              value={stagedRoommatePhone}
              onValueChange={(val) => setStagedRoommatePhone(val)}
              disabled={loading}
            />
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>{strings.notifications.roommatesEmail}</Text>
            <Switch
              value={stagedRoommateEmail}
              onValueChange={(val) => setStagedRoommateEmail(val)}
              disabled={loading}
            />
          </View>
        </>
      )}
    </ScrollView>
  );
};

const makeStyles = ({ semantic }: ThemeContextValue) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: semantic.background.primary,
    },
    content: {
      paddingHorizontal: 16,
      paddingVertical: 12,
    },
    sectionTitle: {
      fontSize: 15,
      fontWeight: '700',
      color: semantic.text.tertiary,
      marginBottom: 8,
    },
    notificationsHeader: {
      marginTop: 12,
    },
    roommateHeader: {
      marginTop: 12,
    },
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingVertical: 12,
    },
    label: {
      fontSize: 16,
      color: semantic.text.primary,
      fontWeight: '600',
    },
    linkRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingVertical: 12,
      marginBottom: 20,
    },
    linkLabel: {
      color: semantic.interactive.primary,
      fontWeight: '600',
    },
    linkChevron: {
      color: semantic.interactive.primary,
      fontSize: 18,
      paddingLeft: 8,
    },
  });

export default SettingsModalScreen;


